import React, { useCallback, useEffect, useState } from "react";
import axios from "../../../../../../api/axios";
import CashierWaiterReport from "../components/CashierWaiterReport";
import CategoryReport from "../components/CategoryReport";
import CuisineReport from "../components/CuisineReport";
import CustomerReport from "../components/CustomerReport";
import DiscountReport from "../components/DiscountReport";
import GstReport from "../components/GstReport";
import ItemReport from "../components/ItemReport";
import OrderReport from "../components/OrderReport";
import SectionReport from "../components/SectionReport";
import VoidKotReport from "../components/VoidKotReport";
import {
  buildReportCsvFileName,
  createBillingReportCsvRows,
  downloadCsvFromJson,
} from "../utils/csvExport";

const emptyReportData = {
  orderReport: [],
  itemReport: [],
  waiterReport: [],
  discountReport: [],
  sectionReport: [],
  customerReport: [],
  categoryReport: [],
  cuisineReport: [],
};

const currentDate = () => new Date().toISOString().slice(0, 10);
const restaurantIdFrom = (reportContext) =>
  reportContext?.businessEntityId ||
  reportContext?.business_entity_id ||
  sessionStorage.getItem("configBizEntityId");

const PosBasic = ({ reportContext }) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [startDate, setStartDate] = useState(currentDate);
  const [endDate, setEndDate] = useState(currentDate);
  const [reportData, setReportData] = useState(emptyReportData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const restaurantId = restaurantIdFrom(reportContext);

  const fetchReport = useCallback(async () => {
    if (!restaurantId || !startDate || !endDate) {
      setError("Restaurant and date range are required.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await axios.get("/reports/getDetailedReportPosBasic", {
        params: { restaurantId, startDate, endDate },
      });
      setReportData({ ...emptyReportData, ...response.data });
    } catch (err) {
      console.error("Error fetching POS Basic report:", err);
      setError("Failed to fetch POS Basic report.");
    } finally {
      setLoading(false);
    }
  }, [restaurantId, startDate, endDate]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const tabs = [
    {
      label: "Order Report",
      exportRows: createBillingReportCsvRows("Order Report", reportData),
      content: <OrderReport rows={reportData.orderReport} loading={loading} />,
    },
    {
      label: "Void KOT Report",
      exportRows: createBillingReportCsvRows("Void KOT Report", reportData),
      content: <VoidKotReport rows={reportData.orderReport} loading={loading} />,
    },
    {
      label: "Cashier/Waiter Report",
      exportRows: createBillingReportCsvRows("Cashier/Waiter Report", reportData),
      content: <CashierWaiterReport rows={reportData.waiterReport} loading={loading} />,
    },
    { label: "Cuisine Report", exportRows: createBillingReportCsvRows("Cuisine Report", reportData), content: <CuisineReport rows={reportData.cuisineReport} loading={loading} /> },
    { label: "Category Report", exportRows: createBillingReportCsvRows("Category Report", reportData), content: <CategoryReport rows={reportData.categoryReport} loading={loading} /> },
    { label: "Item Report", exportRows: createBillingReportCsvRows("Item Report", reportData), content: <ItemReport rows={reportData.itemReport} loading={loading} /> },
    { label: "Discount Report", exportRows: createBillingReportCsvRows("Discount Report", reportData), content: <DiscountReport rows={reportData.discountReport} loading={loading} /> },
    { label: "GST Report", exportRows: createBillingReportCsvRows("GST Report", reportData), content: <GstReport rows={reportData.orderReport} loading={loading} /> },
    { label: "Section Report", exportRows: createBillingReportCsvRows("Section Report", reportData), content: <SectionReport rows={reportData.sectionReport} loading={loading} /> },
    { label: "Customer Report", exportRows: createBillingReportCsvRows("Customer Report", reportData), content: <CustomerReport rows={reportData.customerReport} loading={loading} /> },
  ];

  const handleDownloadReport = () => {
    const selectedReport = tabs[selectedTab];
    const fileName = buildReportCsvFileName({
      businessName: reportContext?.businessName,
      productName: reportContext?.productName,
      reportName: selectedReport.label,
      startDate,
      endDate,
    });

    downloadCsvFromJson(selectedReport.exportRows, fileName);
  };

  return (
    <div className="product-report-tabs">
      <div className="product-report-title">
        <div>
          <span>{reportContext?.businessName}</span>
          <strong>{reportContext?.productName}</strong>
        </div>
        <div className="report-filter-bar">
          <label>
            Start Date
            <input
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
            />
          </label>
          <label>
            End Date
            <input
              type="date"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
            />
          </label>
          <button type="button" onClick={fetchReport} disabled={loading}>
            {loading ? "Loading..." : "Load Report"}
          </button>
          <button
            type="button"
            className="report-download-btn"
            onClick={handleDownloadReport}
            disabled={loading}
          >
            Download CSV
          </button>
        </div>
      </div>

      {error && <div className="report-error">{error}</div>}

      <div className="product-report-tab-list">
        {tabs.map((tab, index) => (
          <button
            key={tab.label}
            type="button"
            className={`product-report-tab ${
              selectedTab === index ? "selected" : ""
            }`}
            onClick={() => setSelectedTab(index)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="product-report-tab-panel">{tabs[selectedTab].content}</div>
    </div>
  );
};

export default PosBasic;
